import React, { useMemo } from 'react';
import { segmentDe, MOTS_PAR_SECONDE } from './parseConducteur';

/**
 * LE RELEVÉ — ce que la répétition a réellement duré, slide par slide.
 *
 * POURQUOI IL EXISTE. Les horaires du conducteur (« *3:00 → 4:30* ») ont été
 * écrits à l'estime, et le curseur de lecture s'y cale : quand ils sont faux,
 * c'est le prompteur entier qui ment — il court sur une slide et rampe sur la
 * suivante. On ne peut pas corriger ça de tête, parce que la seule donnée qui
 * tranche est le débit RÉEL de celui qui parle, et il ne se devine pas. Le
 * relevé l'enregistre : on appuie une fois avant de répéter, on parle, et on
 * récupère les vraies durées à recopier dans le conducteur.
 *
 * TROIS PARTIS PRIS :
 *
 *  1. **On horodate au chrono, pas à l'horloge.** Les marques portent le temps
 *     du chrono du prompteur — le même référentiel que le conducteur. Ce qui
 *     sort est donc directement recopiable, sans conversion. Corollaire voulu :
 *     mettre le chrono en pause met le relevé en pause, parce qu'une
 *     interruption n'est pas du temps de parole.
 *  2. **Une marque par ARRIVÉE sur une slide**, et la fin d'une slide est
 *     l'arrivée sur la suivante. Pas de durée pour la slide en cours : elle
 *     n'est pas finie, et afficher une durée qui grandit n'apprend rien.
 *  3. **Le débit mesuré est la vraie sortie.** Les durées disent ce qui s'est
 *     passé ; le débit en mots/s dit pourquoi, et c'est lui qui se transpose
 *     aux slides qu'on n'a pas répétées. `MOTS_PAR_SECONDE` (2,5) est une
 *     hypothèse — le relevé la remplace par une mesure.
 *
 * ⚠️ UNE MARQUE `num: null` EST UNE FIN DE SEGMENT, pas une slide. Elle est
 * posée à l'arrêt de l'enregistrement : elle borne la dernière slide dite, et
 * elle coupe le chaînage pour qu'une reprise plus tard ne fabrique pas une
 * durée absurde à cheval sur la coupure.
 */

const mmss = (s) => {
    const a = Math.abs(Math.round(s));
    return `${Math.floor(a / 60)}:${String(a % 60).padStart(2, '0')}`;
};
const signe = (s) => `${s >= 0 ? '+' : '−'}${mmss(s)}`;
const deci = (x) => x.toFixed(2).replace('.', ',');

/**
 * Les marques brutes → une ligne par passage sur une slide.
 *
 * @param marques  [{num, t}] dans l'ordre, `num: null` = fin de segment
 * @param minutage le retour de `minutageParSlide`
 * @param parSlide sections indexées par numéro de slide
 */
export function analyser(marques, minutage, parSlide) {
    const lignes = [];
    const vus = new Map();

    for (let i = 0; i < marques.length; i++) {
        const m = marques[i];
        const suivant = marques[i + 1];
        // Une sentinelle ne commence rien ; une slide sans suivante est en
        // cours, donc sans durée connue.
        if (m.num == null || !suivant) continue;

        const reel = suivant.t - m.t;
        /* ⚠️ UNE DURÉE NÉGATIVE N'EST PAS UNE MESURE. Le chrono peut reculer
           pendant un enregistrement — « ⟲ » le remet à zéro, « ⇥ caler » le
           pose sur le début prévu de la slide, et les deux servent justement
           en répétition. La marque suivante tombe alors avant la précédente.
           On jette le passage plutôt que d'afficher un temps impossible ; les
           passages d'après, eux, sont de nouveau cohérents entre eux. */
        if (reel < 0) continue;
        const section = parSlide[m.num];
        let mots = null;
        if (section && section.bornes) {
            const seg = segmentDe(section, m.num);
            mots = Math.round(seg.fin - seg.debut);
        }
        const finPrevue = minutage.attendu[m.num];
        const debutPrevu = minutage.debut[m.num];
        const prevu = finPrevue != null && debutPrevu != null ? finPrevue - debutPrevu : null;

        vus.set(m.num, (vus.get(m.num) || 0) + 1);
        lignes.push({
            num: m.num,
            passage: vus.get(m.num),
            debut: m.t,
            fin: suivant.t,
            reel,
            prevu,
            ecart: prevu != null ? reel - prevu : null,
            mots,
            debit: mots != null && reel > 0 ? mots / reel : null,
            titre: section?.titre || ''
        });
    }

    const repasses = [...vus.entries()].filter(([, n]) => n > 1).map(([n]) => n);
    const avecMots = lignes.filter((l) => l.mots != null && l.reel > 0);
    const totalMots = avecMots.reduce((t, l) => t + l.mots, 0);
    const totalTemps = avecMots.reduce((t, l) => t + l.reel, 0);

    return {
        lignes,
        repasses,
        debitMoyen: totalTemps > 0 ? totalMots / totalTemps : null,
        duree: lignes.length ? lignes[lignes.length - 1].fin - lignes[0].debut : 0
    };
}

/** Le relevé en markdown : un tableau à lire, puis les minutages à recopier. */
function enMarkdown({ lignes, debitMoyen, duree }) {
    const t = [
        '| Slide | Réel | Prévu | Écart | Mots | Débit réel |',
        '|---|---|---|---|---|---|',
        ...lignes.map((l) => `| ${l.num}${l.passage > 1 ? ` (${l.passage}e)` : ''} | ${mmss(l.reel)} | ${l.prevu != null ? mmss(l.prevu) : '—'} | ${l.ecart != null ? signe(l.ecart) : '—'} | ${l.mots ?? '—'} | ${l.debit != null ? deci(l.debit) : '—'} |`)
    ].join('\n');

    const minutages = lignes
        .filter((l) => l.passage === 1)
        .map((l) => `SLIDE ${l.num} · *${mmss(l.debut)} → ${mmss(l.fin)}*`)
        .join('\n');

    return [
        `# Relevé de répétition — ${new Date().toLocaleString('fr-FR')}`,
        '',
        `Durée totale : **${mmss(duree)}** · débit mesuré : **${debitMoyen != null ? deci(debitMoyen) : '—'} mots/s**`,
        `(hypothèse du prompteur : ${deci(MOTS_PAR_SECONDE)} mots/s)`,
        '',
        t,
        '',
        '## Minutages à recopier dans le conducteur',
        '',
        '```',
        minutages,
        '```',
        ''
    ].join('\n');
}

export default function ReleveTimings({ marques, minutage, parSlide, onFermer, onEffacer }) {
    const analyse = useMemo(() => analyser(marques, minutage, parSlide), [marques, minutage, parSlide]);
    const { lignes, repasses, debitMoyen, duree } = analyse;

    const copier = () => {
        navigator.clipboard?.writeText(enMarkdown(analyse));
    };

    const telecharger = () => {
        const blob = new Blob([enMarkdown(analyse)], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `releve-repetition-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.md`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="absolute inset-0 z-30 bg-slate-900/40 flex items-center justify-center p-6">
            <div className="bg-white rounded-lg shadow-2xl border border-slate-300 w-full max-w-4xl max-h-full flex flex-col">
                <header className="flex-shrink-0 px-5 py-3 border-b border-slate-200 flex items-center gap-4">
                    <h2 className="text-lg font-bold text-slate-900">Relevé de répétition</h2>
                    {lignes.length > 0 && (
                        <span className="text-sm text-slate-500 tabular-nums">
                            {lignes.length} slides · {mmss(duree)}
                        </span>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={copier}
                            disabled={!lignes.length}
                            className="px-3 h-8 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 disabled:opacity-40"
                            title="Copier le relevé en markdown — tableau + minutages à recopier"
                        >
                            ⧉ copier
                        </button>
                        <button
                            onClick={telecharger}
                            disabled={!lignes.length}
                            className="px-3 h-8 rounded border border-slate-200 bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 disabled:opacity-40"
                        >
                            ↓ .md
                        </button>
                        <button
                            onClick={onEffacer}
                            disabled={!marques.length}
                            className="px-3 h-8 rounded border border-rose-200 bg-rose-50 hover:bg-rose-100 text-xs font-semibold text-rose-700 disabled:opacity-40"
                            title="Effacer le relevé et repartir d'une répétition vierge"
                        >
                            effacer
                        </button>
                        <button
                            onClick={onFermer}
                            className="px-3 h-8 rounded bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold"
                        >
                            fermer
                        </button>
                    </div>
                </header>

                {/* Le débit mesuré est la conclusion, pas un détail : il est en
                    tête, et confronté à l'hypothèse qu'il remplace. */}
                {debitMoyen != null && (
                    <div className="flex-shrink-0 px-5 py-3 bg-sky-50 border-b border-sky-200 text-sm text-slate-700">
                        Débit mesuré : <strong className="tabular-nums">{deci(debitMoyen)} mots/s</strong>
                        <span className="text-slate-500">
                            {' '}· le prompteur suppose {deci(MOTS_PAR_SECONDE)} mots/s
                        </span>
                        {Math.abs(debitMoyen - MOTS_PAR_SECONDE) > 0.25 && (
                            <span className="ml-2 text-sky-800 font-semibold">
                                → ajuster <code className="px-1 bg-white border border-sky-200 rounded">MOTS_PAR_SECONDE</code> à {deci(debitMoyen)}
                            </span>
                        )}
                    </div>
                )}
                {repasses.length > 0 && (
                    <div className="flex-shrink-0 px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
                        ⚠️ Slides dites plusieurs fois : {repasses.join(', ')} — chaque passage a sa ligne.
                    </div>
                )}

                <div className="flex-1 overflow-y-auto">
                    {lignes.length === 0 ? (
                        <p className="p-8 text-slate-400 text-sm">
                            Aucune slide enregistrée. Lance l'enregistrement (touche <kbd className="px-1 border border-slate-300 rounded">e</kbd>),
                            puis déroule ta répétition : chaque changement de slide est horodaté.
                        </p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left px-5 py-2 font-semibold">Slide</th>
                                    <th className="text-right px-3 py-2 font-semibold">Réel</th>
                                    <th className="text-right px-3 py-2 font-semibold">Prévu</th>
                                    <th className="text-right px-3 py-2 font-semibold">Écart</th>
                                    <th className="text-right px-3 py-2 font-semibold">Mots</th>
                                    <th className="text-right px-5 py-2 font-semibold">Débit</th>
                                </tr>
                            </thead>
                            <tbody className="tabular-nums">
                                {lignes.map((l, i) => (
                                    <tr key={`${l.num}-${l.passage}-${i}`} className="border-b border-slate-100">
                                        <td className="px-5 py-1.5">
                                            <span className="font-bold text-sky-700">{l.num}</span>
                                            {l.passage > 1 && <span className="text-xs text-amber-700 ml-1">({l.passage}e)</span>}
                                            <span className="ml-3 text-slate-500 text-xs normal-case">{l.titre}</span>
                                        </td>
                                        <td className="text-right px-3 py-1.5 font-semibold">{mmss(l.reel)}</td>
                                        <td className="text-right px-3 py-1.5 text-slate-400">{l.prevu != null ? mmss(l.prevu) : '—'}</td>
                                        <td className="text-right px-3 py-1.5">
                                            {l.ecart == null ? '—' : (
                                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${l.ecart > 15 ? 'bg-amber-100 text-amber-800'
                                                    : l.ecart < -15 ? 'bg-emerald-100 text-emerald-800'
                                                        : 'bg-slate-100 text-slate-500'
                                                    }`}>
                                                    {signe(l.ecart)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-right px-3 py-1.5 text-slate-400">{l.mots ?? '—'}</td>
                                        <td className="text-right px-5 py-1.5 text-slate-600">{l.debit != null ? deci(l.debit) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
